import { useState, useEffect, useRef, useCallback } from 'react';

export const useProctoring = (config = {}) => {
  const {
    enableTabSwitchDetection = true,
    enableCopyPasteDetection = true,
    enableFullscreenEnforcement = true,
    enableRightClickDisable = true,
    enableKeyboardShortcuts = true,
    enableCameraMonitoring = false,
    enableMicrophoneMonitoring = false,
    maxViolations = 5,
    onViolation,
    onMaxViolationsReached
  } = config;

  const [proctoringStatus, setProctoringStatus] = useState({
    isActive: false,
    violations: [],
    violationCount: 0,
    isFullscreen: false,
    windowFocused: true,
    cameraStream: null,
    microphoneStream: null
  });

  const violationTimeoutRef = useRef(null);
  const lastViolationRef = useRef(0);

  const addViolation = useCallback((violation) => {
    const now = Date.now();
    // Prevent spam violations (max 1 per second)
    if (now - lastViolationRef.current < 1000) return;

    lastViolationRef.current = now;

    setProctoringStatus(prev => {
      const newViolations = [...prev.violations, violation];
      const newCount = prev.violationCount + 1;

      // Call violation callback
      if (onViolation) onViolation(violation);

      // Check max violations
      if (newCount >= maxViolations) {
        if (onMaxViolationsReached) onMaxViolationsReached();
      }

      return {
        ...prev,
        violations: newViolations,
        violationCount: newCount
      };
    });
  }, [onViolation, onMaxViolationsReached, maxViolations]);

  // Tab switch / window focus detection
  useEffect(() => {
    if (!proctoringStatus.isActive || !enableTabSwitchDetection) return;

    const handleWindowBlur = () => {
      setProctoringStatus(prev => ({ ...prev, windowFocused: false }));
      addViolation({
        type: 'window_blur',
        timestamp: new Date(),
        details: 'Window lost focus - possible tab switch or application change'
      });
    };

    const handleWindowFocus = () => {
      setProctoringStatus(prev => ({ ...prev, windowFocused: true }));
    };

    const handleVisibilityChange = () => {
      if (document.hidden) {
        addViolation({
          type: 'tab_switch',
          timestamp: new Date(),
          details: 'Page became hidden - tab switch detected'
        });
      }
    };

    window.addEventListener('blur', handleWindowBlur);
    window.addEventListener('focus', handleWindowFocus);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('blur', handleWindowBlur);
      window.removeEventListener('focus', handleWindowFocus);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [proctoringStatus.isActive, enableTabSwitchDetection, addViolation]);

  // Copy/Paste detection
  useEffect(() => {
    if (!proctoringStatus.isActive || !enableCopyPasteDetection) return;

    const handleCopy = (e) => {
      e.preventDefault();
      addViolation({
        type: 'copy_paste',
        timestamp: new Date(),
        details: 'Copy operation detected and blocked'
      });
    };

    const handlePaste = (e) => {
      e.preventDefault();
      addViolation({
        type: 'copy_paste',
        timestamp: new Date(),
        details: 'Paste operation detected and blocked'
      });
    };

    const handleCut = (e) => {
      e.preventDefault();
      addViolation({
        type: 'copy_paste',
        timestamp: new Date(),
        details: 'Cut operation detected and blocked'
      });
    };

    document.addEventListener('copy', handleCopy);
    document.addEventListener('paste', handlePaste);
    document.addEventListener('cut', handleCut);

    return () => {
      document.removeEventListener('copy', handleCopy);
      document.removeEventListener('paste', handlePaste);
      document.removeEventListener('cut', handleCut);
    };
  }, [proctoringStatus.isActive, enableCopyPasteDetection, addViolation]);

  // Right-click disable
  useEffect(() => {
    if (!proctoringStatus.isActive || !enableRightClickDisable) return;

    const handleContextMenu = (e) => {
      e.preventDefault();
      addViolation({
        type: 'right_click',
        timestamp: new Date(),
        details: 'Right-click context menu attempted'
      });
    };

    document.addEventListener('contextmenu', handleContextMenu);

    return () => {
      document.removeEventListener('contextmenu', handleContextMenu);
    };
  }, [proctoringStatus.isActive, enableRightClickDisable, addViolation]);

  // Keyboard shortcuts detection
  useEffect(() => {
    if (!proctoringStatus.isActive || !enableKeyboardShortcuts) return;

    const handleKeyDown = (e) => {
      const forbiddenCombinations = [
        { ctrl: true, key: 'c' }, // Copy
        { ctrl: true, key: 'v' }, // Paste
        { ctrl: true, key: 'x' }, // Cut
        { ctrl: true, key: 'a' }, // Select All
        { ctrl: true, key: 's' }, // Save
        { ctrl: true, key: 'p' }, // Print
        { ctrl: true, key: 'f' }, // Find
        { ctrl: true, key: 'h' }, // History
        { ctrl: true, key: 'j' }, // Downloads
        { ctrl: true, key: 'u' }, // View Source
        { ctrl: true, shift: true, key: 'i' }, // Dev Tools
        { ctrl: true, shift: true, key: 'j' }, // Console
        { ctrl: true, shift: true, key: 'c' }, // Inspector
        { key: 'F12' }, // Dev Tools
        { alt: true, key: 'Tab' }, // Alt+Tab
        { key: 'F5' }, // Refresh
        { ctrl: true, key: 'r' }, // Refresh
        { ctrl: true, key: 't' }, // New Tab
        { ctrl: true, key: 'n' }, // New Window
        { ctrl: true, key: 'w' }, // Close Tab
        { ctrl: true, shift: true, key: 'n' }, // Incognito
      ];

      const isForbidden = forbiddenCombinations.some(combo => {
        return Object.keys(combo).every(key => {
          if (key === 'ctrl') return e.ctrlKey === combo.ctrl;
          if (key === 'alt') return e.altKey === combo.alt;
          if (key === 'shift') return e.shiftKey === combo.shift;
          if (key === 'key') return e.key === combo.key || e.code === combo.key;
          return false;
        });
      });

      if (isForbidden) {
        e.preventDefault();
        e.stopPropagation();
        addViolation({
          type: 'key_combination',
          timestamp: new Date(),
          details: `Forbidden key combination: ${e.ctrlKey ? 'Ctrl+' : ''}${e.altKey ? 'Alt+' : ''}${e.shiftKey ? 'Shift+' : ''}${e.key}`
        });
      }
    };

    document.addEventListener('keydown', handleKeyDown, true);

    return () => {
      document.removeEventListener('keydown', handleKeyDown, true);
    };
  }, [proctoringStatus.isActive, enableKeyboardShortcuts, addViolation]);

  // Fullscreen enforcement
  useEffect(() => {
    if (!proctoringStatus.isActive || !enableFullscreenEnforcement) return;

    const handleFullscreenChange = () => {
      const isFullscreen = !!document.fullscreenElement;
      setProctoringStatus(prev => ({ ...prev, isFullscreen }));

      if (!isFullscreen && proctoringStatus.isActive) {
        addViolation({
          type: 'fullscreen_exit',
          timestamp: new Date(),
          details: 'Exited fullscreen mode'
        });

        // Note: Cannot automatically re-enter fullscreen due to browser security restrictions
        // Fullscreen API can only be initiated by a user gesture (click, etc.)
        // The user will need to manually click a button to re-enter fullscreen
        console.warn('Fullscreen exited. User must manually re-enter fullscreen via a button click.');
      }
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
    };
  }, [proctoringStatus.isActive, enableFullscreenEnforcement, addViolation]);

  // Camera and Microphone monitoring
  const initializeMediaStreams = useCallback(async () => {
    try {
      const constraints = {
        video: enableCameraMonitoring,
        audio: enableMicrophoneMonitoring
      };

      if (enableCameraMonitoring || enableMicrophoneMonitoring) {
        const stream = await navigator.mediaDevices.getUserMedia(constraints);

        setProctoringStatus(prev => ({
          ...prev,
          cameraStream: enableCameraMonitoring ? stream : null,
          microphoneStream: enableMicrophoneMonitoring ? stream : null
        }));
      }
    } catch (error) {
      console.error('Failed to initialize media streams:', error);
    }
  }, [enableCameraMonitoring, enableMicrophoneMonitoring]);

  const startProctoring = useCallback(async () => {
    // Initialize media streams first (if needed)
    if (enableCameraMonitoring || enableMicrophoneMonitoring) {
      await initializeMediaStreams();
    }

    // Request fullscreen if enabled - this should be called from a user gesture
    // Don't throw error if fullscreen fails - just log it
    if (enableFullscreenEnforcement) {
      try {
        // Check if we can request fullscreen
        if (document.documentElement.requestFullscreen) {
          await document.documentElement.requestFullscreen().catch((error) => {
            // Fullscreen may fail due to browser restrictions (not a user gesture, etc.)
            // This is not a critical error - proctoring can still work without fullscreen
            console.warn('Could not enter fullscreen (this is not critical):', error.message);
          });
        } else {
          console.warn('Fullscreen API not supported');
        }
      } catch (error) {
        // Fullscreen may fail due to browser restrictions (not a user gesture, etc.)
        // This is not a critical error - proctoring can still work without fullscreen
        console.warn('Could not enter fullscreen (this is not critical):', error.message);
      }
    }

    // Always activate proctoring regardless of fullscreen state
    setProctoringStatus(prev => ({
      ...prev,
      isActive: true,
      violations: [],
      violationCount: 0,
      isFullscreen: !!document.fullscreenElement
    }));
  }, [enableFullscreenEnforcement, enableCameraMonitoring, enableMicrophoneMonitoring, initializeMediaStreams]);

  const stopProctoring = useCallback(() => {
    // Exit fullscreen
    if (document.fullscreenElement) {
      document.exitFullscreen();
    }

    setProctoringStatus(prev => {
      // Stop media streams
      if (prev.cameraStream) {
        prev.cameraStream.getTracks().forEach(track => track.stop());
      }
      if (prev.microphoneStream) {
        prev.microphoneStream.getTracks().forEach(track => track.stop());
      }

      return {
        ...prev,
        isActive: false,
        cameraStream: null,
        microphoneStream: null
      };
    });
  }, []);

  const clearViolations = useCallback(() => {
    setProctoringStatus(prev => ({
      ...prev,
      violations: [],
      violationCount: 0
    }));
  }, []);

  // Function to request fullscreen - must be called from a user gesture (button click)
  const requestFullscreen = useCallback(async () => {
    if (!enableFullscreenEnforcement) return;

    try {
      if (document.documentElement.requestFullscreen) {
        await document.documentElement.requestFullscreen();
        setProctoringStatus(prev => ({ ...prev, isFullscreen: true }));
      } else {
        console.warn('Fullscreen API not supported');
      }
    } catch (error) {
      console.error('Failed to enter fullscreen:', error.message);
      throw error;
    }
  }, [enableFullscreenEnforcement]);

  return {
    proctoringStatus,
    startProctoring,
    stopProctoring,
    clearViolations,
    addViolation,
    requestFullscreen
  };
};

